import L from "leaflet";
import type { LatLngExpression } from "leaflet";

/**
 * Offsets a polyline to the right using Leaflet's built-in geometry
 */
export function offsetPolyline(
  positions: LatLngExpression[],
  offsetMeters: number = 8,
): LatLngExpression[] {
  try {
    if (positions.length < 2) return positions;

    // Convert to Leaflet LatLng objects
    const latLngs = positions.map((pos) => {
      if (Array.isArray(pos)) {
        return L.latLng(pos[0] as number, pos[1] as number);
      }
      const p = pos as any;
      return L.latLng(p.lat, p.lng || p.lon);
    });

    // Calculate offset for each segment
    const offsetLatLngs: L.LatLng[] = [];

    for (let i = 0; i < latLngs.length; i++) {
      const current = latLngs[i];

      // For first and last points, use the adjacent segment
      const prev = i > 0 ? latLngs[i - 1] : latLngs[i];
      const next = i < latLngs.length - 1 ? latLngs[i + 1] : latLngs[i];

      // Calculate bearing (direction) of the line segment
      let bearing: number;
      if (i === 0) {
        // First point: use direction to next point
        bearing = getBearing(current, next);
      } else if (i === latLngs.length - 1) {
        // Last point: use direction from previous point
        bearing = getBearing(prev, current);
      } else {
        // Middle points: average of incoming and outgoing bearings
        const bearingIn = getBearing(prev, current);
        const bearingOut = getBearing(current, next);
        bearing = (bearingIn + bearingOut) / 2;
      }

      // Offset perpendicular to the right (add 90 degrees)
      const offsetBearing = bearing + 90;

      // Calculate new position
      const offsetPoint = destinationPoint(current, offsetMeters, offsetBearing);
      offsetLatLngs.push(offsetPoint);
    }

    // Convert back to LatLngExpression format
    return offsetLatLngs.map((latLng) => [latLng.lat, latLng.lng]);
  } catch (error) {
    console.warn("Failed to offset polyline:", error);
    return positions;
  }
}

/**
 * Calculate bearing between two points in degrees
 */
function getBearing(from: L.LatLng, to: L.LatLng): number {
  const lat1 = toRadians(from.lat);
  const lat2 = toRadians(to.lat);
  const dLon = toRadians(to.lng - from.lng);

  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

  const bearing = Math.atan2(y, x);
  return (toDegrees(bearing) + 360) % 360;
}

/**
 * Calculate destination point given distance and bearing
 */
function destinationPoint(point: L.LatLng, distance: number, bearing: number): L.LatLng {
  const R = 6371000; // Earth's radius in meters
  const δ = distance / R; // angular distance
  const θ = toRadians(bearing);

  const φ1 = toRadians(point.lat);
  const λ1 = toRadians(point.lng);

  const φ2 = Math.asin(Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ));

  const λ2 =
    λ1 +
    Math.atan2(Math.sin(θ) * Math.sin(δ) * Math.cos(φ1), Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2));

  return L.latLng(toDegrees(φ2), toDegrees(λ2));
}

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

function toDegrees(radians: number): number {
  return (radians * 180) / Math.PI;
}
